/**
 * DEV-260/261 — grava uma etapa e segue (FR-010): uma requisição por vez,
 * respostas mescladas no cache, rascunho da etapa limpo, próxima etapa (ou o
 * Resumo). Erros: 422 com `issues` volta para a tela; sessão expirada leva ao
 * Onboarding; demais viram Sonner não crítico.
 */
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@eureca/ui';
import { AlignmentApiError, alignmentApi, type AnswerIssue } from '../shared/api';
import type { FormDefinition, JobDescription } from '../shared/definition';
import { clearDraftKeys } from '../shared/draft';
import type { Answers } from '../shared/form-rules';
import { TOAST } from '../shared/messages';
import { omitKeys } from '../shared/objects';
import { nextPageName, previousPageName } from '../shared/page-rules';
import { clearAlignmentSession } from '../shared/session';
import { definitionQueryKey, descriptionQueryKey } from '../shared/use-form-session';

const NON_CRITICAL_TOAST_MS = 6000;

export function nonCriticalToast(message: string) {
  toast(message, {
    duration: NON_CRITICAL_TOAST_MS,
    action: { label: TOAST.close, onClick: () => undefined },
  });
}

export interface StepContext {
  tenant: string;
  formId: string;
  pageName: string;
  definition: FormDefinition;
  description: JobDescription;
  sessionToken: string;
  returnToSummary?: boolean;
}

export function useStepSave(ctx: StepContext, onIssues: (issues: AnswerIssue[]) => void) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inFlight = useRef(false);
  const { tenant, formId, pageName, definition, description, sessionToken } = ctx;

  const mutation = useMutation({
    mutationFn: async (body: Answers) => {
      await alignmentApi.savePage(sessionToken, pageName, body);
      return body;
    },
    onSettled: () => {
      inFlight.current = false;
    },
    onSuccess: (body) => {
      queryClient.setQueryData<JobDescription>(descriptionQueryKey(description.id), (prev) => {
        if (!prev) return prev;
        const cleared = Object.keys(body).filter((k) => body[k] === null);
        const filled = Object.fromEntries(Object.entries(body).filter(([, v]) => v !== null));
        return { ...prev, answers: omitKeys({ ...prev.answers, ...filled }, cleared) };
      });
      clearDraftKeys(description.id, Object.keys(body));
      const next = ctx.returnToSummary ? null : nextPageName(definition, pageName);
      if (next) {
        void navigate({
          to: '/descricao-vaga/$tenant/$formId/etapa/$pageName',
          params: { tenant, formId, pageName: next },
        });
      } else {
        void navigate({ to: '/descricao-vaga/$tenant/$formId/resumo', params: { tenant, formId } });
      }
    },
  });

  function handleError(err: unknown) {
    if (!(err instanceof AlignmentApiError)) return nonCriticalToast(TOAST.server);
    if (err.code === 'session_expired') {
      clearAlignmentSession(formId);
      void navigate({
        to: '/descricao-vaga/$tenant/$formId',
        params: { tenant, formId },
        replace: true,
      });
      return;
    }
    if (err.code === 'already_submitted') {
      void navigate({ to: '/descricao-vaga/$tenant/$formId/sucesso', params: { tenant, formId } });
      return;
    }
    if (err.code === 'hidden_question') {
      // A consultora mudou a configuração no meio do preenchimento: relê a definição.
      void queryClient.invalidateQueries({ queryKey: definitionQueryKey(description.id) });
      return nonCriticalToast(TOAST.formChanged);
    }
    if (err.issues.length > 0) return onIssues(err.issues);
    if (err.status === 0) return nonCriticalToast(TOAST.noConnection);
    if (err.status === 429) return nonCriticalToast(TOAST.tooManyAttempts);
    nonCriticalToast(TOAST.server);
  }

  return {
    isPending: mutation.isPending,
    /** Começa a gravação; `false` quando já havia uma em curso (duplo clique). */
    begin(): boolean {
      if (inFlight.current || mutation.isPending) return false;
      inFlight.current = true;
      return true;
    },
    end() {
      inFlight.current = false;
    },
    /** Grava; a promessa rejeita quando falhou (a tela continua igual). */
    async save(body: Answers): Promise<void> {
      try {
        await mutation.mutateAsync(body);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    goBack() {
      if (ctx.returnToSummary) {
        void navigate({ to: '/descricao-vaga/$tenant/$formId/resumo', params: { tenant, formId } });
        return;
      }
      const previous = previousPageName(definition, pageName);
      if (previous) {
        void navigate({
          to: '/descricao-vaga/$tenant/$formId/etapa/$pageName',
          params: { tenant, formId, pageName: previous },
        });
      }
    },
  };
}
